import { z } from 'zod'
import {
  countryCodeEnumSchema,
  UNKNOWN_COUNTRY_CODE,
} from '@/constants/country-code'
import {
  basicInfoSchema,
  collaborationInfoSchema,
  customizedFieldsSchema,
  periodSchema,
  platformSchema,
  socialAdvanceStatisticsSchema,
  socialBasicStatisticsSchema,
} from '@/types/schema/api/kol-collections/collection-setting'
import { listResourceSchema } from '@/types/schema/api/list-resource-schema'
import { genderSchema } from '@/types/schema/common/gender-schema'
import potentialSchema from '@/types/schema/kol/potential'
import { availableUnlockDataSchema } from '@/types/schema/kol/unlock'
import { PlatformShortcode } from '@/utils/convert-platform'

const linkSchema = z.object({
  platform_type: z.nativeEnum(PlatformShortcode),
  platform_id: z.string(),
})
export type Link = z.infer<typeof linkSchema>

const kolInfoSchema = z.object({
  id: z.number(),
  uuid: z.string().uuid(),
  follower_count: z.number(),
  name: z.string(),
  gender: genderSchema.nullable(),
  potential: potentialSchema.nullable(),
  customized_tags_names: z.array(z.string()),
  country_code: z.literal(UNKNOWN_COUNTRY_CODE).or(countryCodeEnumSchema), // 跨國網紅的 country code 有可能是 unknown，且是預期的 ref: https://www.notion.so/ikala/Staging-Search-105fb0834d6e807092f6cc8d0d160215?pvs=4
  links: z.array(linkSchema),
  is_verified: z.boolean(),
  is_radar_star: z.boolean(),
})

const collectionKolSchema = z.object({
  collection_id: z.number(),
  id: z.string(),
  kol_id: z.string().uuid(),
  kol_info: kolInfoSchema,
  kol_name: z.string(),
  is_locked: z.boolean(),
  is_expired: z.boolean().optional().default(false),
  statistics_status: availableUnlockDataSchema.array().optional().default([]),
})
export type CollectionKol = z.infer<typeof collectionKolSchema>

export const kolCollectionSchema = z.object({
  id: z.number(),
  name: z.string(),
  collection_kols: z.array(collectionKolSchema),
})
export type KolCollection = z.infer<typeof kolCollectionSchema>

const queryCollectedKolSchema = z
  .object({
    collection_kol_id: z.string(),
  })
  .nullable()

/**
 * @description 這個 schema 存在是因為 list 與單一個的 collection schema 不同
 */
export const kolCollectionsCollectionSchema = z.object({
  id: z.number(),
  name: z.string(),
  kol_amount: z.number(),
  last_updated: z.string().datetime(),
  owner_id: z.number(),
  owner_name: z.string(),
  query_collected_kol: queryCollectedKolSchema,
})
export type KolCollectionsCollection = z.infer<
  typeof kolCollectionsCollectionSchema
>

// uuid only for cache. ref: https://gitlab.corp.ikala.tv/kolradar/buggy/-/merge_requests/2465
export type CreateKolCollectionRequest = { name: string; uuid?: string }
export const createKolCollectionResponseSchema = kolCollectionSchema.pick({
  id: true,
})
export type CreateKolCollectionResponse = z.infer<
  typeof createKolCollectionResponseSchema
>

export type DeleteKolCollectionRequest = { id: number }

export type UpdateKolCollectionRequest = { id: number; name: string }

export type AddKolsToKolCollectionRequest = {
  id: number
  kol_ids: string[]
}

export const fetchKolCollectionsResponseSchema = listResourceSchema.extend({
  data: z.array(kolCollectionsCollectionSchema),
})
export type FetchKolCollectionsResponse = z.infer<
  typeof fetchKolCollectionsResponseSchema
>

export type FetchKolCollectionsRequest = {
  page: number
  name?: string
  sort_by?: string[]
  owner_ids?: number[]
  filter_kol_uuid?: string
  kol_uuid?: string
}

export type FetchKolCollectionsInfiniteRequest = Omit<
  FetchKolCollectionsRequest,
  'page'
>
export type DuplicateKolCollectionRequest = { id: number; name: string }

export type FetchKolCollectionRequest = { id: number }
export type FetchKolCollectionResponse = z.infer<typeof kolCollectionSchema>

export type DeleteKolsFromKolCollectionRequest = {
  id: number
  collection_kol_ids: string[]
  kol_ids: string[]
  /**
   * isCollectionKol 用於 optimistic update
   */
  isCollectionKol?: boolean
}

export type FetchKolCollectionSettingRequest = { collectionId: number }
const KolCollectionSettingSchema = z.object({
  platform: platformSchema,
  period: periodSchema,
  basic_info: basicInfoSchema,
  social_basic_statistics: socialBasicStatisticsSchema,
  social_advance_statistics: socialAdvanceStatisticsSchema,
  collaboration_info: collaborationInfoSchema,
  customized_fields: customizedFieldsSchema,
})
export const fetchKolCollectionSettingResponseSchema =
  KolCollectionSettingSchema
export type FetchKolCollectionSettingResponse = z.infer<
  typeof fetchKolCollectionSettingResponseSchema
>

export type SetKolCollectionSettingRequest = {
  id: number
  payload: z.infer<typeof KolCollectionSettingSchema>
}

export type ExportKolCollectionRequest = { id: number }
